/* eslint no-console: 0 */
/* global axios */
import ApiClient from '../ApiClient';

export const buildCreatePayload = ({
  message,
  isPrivate,
  contentType,
  contentAttributes,
  additionalAttributes,
  echoId,
  files,
  ccEmails = '',
  bccEmails = '',
  toEmails = '',
  templateParams,
}) => {
  let payload;
  const isHttpUrl = value =>
    typeof value === 'string' && /^https?:\/\//i.test(value);

  const addButtonsComponents = (params) => {
    if (params.processed_params) {
      const buttons = [];

      Object.entries(params.processed_params).forEach(([key, value]) => {
        const urlMatch = key.match(/^BUTTON_(\d+)$/);
        if (urlMatch) {
          buttons.push({
            type: 'button',
            sub_type: 'url',
            index: urlMatch[1],
            parameters: [
              {
                type: 'text',
                text: value,
              },
            ],
          });
          delete params.processed_params[key];
          return;
        }

        const copyCodeMatch = key.match(/^COPY_CODE_BUTTON_(\d+)$/);
        if (copyCodeMatch) {
          buttons.push({
            type: 'button',
            sub_type: 'copy_code',
            index: copyCodeMatch[1],
            parameters: [
              {
                type: 'coupon_code',
                coupon_code: value,
              },
            ],
          });
          delete params.processed_params[key];
        }
      });

      const existingButtons = Array.isArray(params.button_components) ? params.button_components : [];
      params.button_components = [...existingButtons, ...buttons];
    }
  };

  const addHeaderComponents = (params) => {
    if (params.processed_params) {
      if (params.header_media?.blob_id && params.header_media?.type) {
        const mediaType = params.header_media.type;
        params.header_components = {
          type: mediaType,
          [mediaType]: {
            blob_id: params.header_media.blob_id,
            blob_key: params.header_media.blob_key,
            mime_type: params.header_media.mime_type,
            filename: params.header_media.filename,
            byte_size: params.header_media.byte_size,
            file_url: params.header_media.file_url,
          },
        };
      } else if (params.processed_params.VIDEO && isHttpUrl(params.processed_params.VIDEO)) {
        params.header_components = {
          type: 'video',
          video: {
            link: params.processed_params.VIDEO,
          },
        };
      } else if (params.processed_params.IMAGE && isHttpUrl(params.processed_params.IMAGE)) {
        params.header_components = {
          type: 'image',
          image: {
            link: params.processed_params.IMAGE,
          },
        };
      } else if (params.processed_params.DOCUMENT && isHttpUrl(params.processed_params.DOCUMENT)) {
        params.header_components = {
          type: 'document',
          document: {
            link: params.processed_params.DOCUMENT,
          },
        };
      }else {

        const headerKeys = Object.keys(params.processed_params).filter(key => key.startsWith('TEXT_'));

        if (headerKeys.length > 0) {
          const headerKey = headerKeys[0]; // Puedes ajustar esto si necesitas más de uno
          const paramId = headerKey.slice(5); // Extrae lo que viene después de 'TEXT_'
          const value = params.processed_params[headerKey];
        
          // Construir el header_components
          if (/^\d+$/.test(paramId)) {
            // Es un parámetro posicional (ej: TEXT_1)
            params.header_components = {
              type: 'text',
              text: value
            };
          } else {
            // Es un parámetro nombrado (ej: TEXT_nombre)
            params.header_components = {
              type: 'text',
              text: value,
              parameter_name: paramId
            };
          }
        }

      }
    }
  };

  if (files && files.length !== 0) {
    payload = new FormData();
    if (contentType) {
      payload.append('content_type', contentType);
    }
    if (message) {
      payload.append('content', message);
    }
    files.forEach(file => {
      payload.append('attachments[]', file);
    });
    payload.append('private', isPrivate);
    payload.append('echo_id', echoId);
    payload.append('cc_emails', ccEmails);
    payload.append('bcc_emails', bccEmails);
    if (toEmails) {
      payload.append('to_emails', toEmails);
    }
    if (contentAttributes) {
      payload.append('content_attributes', JSON.stringify(contentAttributes));
    }
    if (additionalAttributes) {
      payload.append('additional_attributes', JSON.stringify(additionalAttributes));
    }
    if (templateParams) {
      
      addHeaderComponents(templateParams);
      addButtonsComponents(templateParams);
      payload.append('template_params', JSON.stringify(templateParams));
    }
  } else {
    if (templateParams) {
      addHeaderComponents(templateParams);

      addButtonsComponents(templateParams);
      // Validate if processed_params exists before deleting its properties
      if (templateParams.processed_params) {
        if (templateParams.processed_params.DOCUMENT) {
          delete templateParams.processed_params.DOCUMENT;
        }
        if (templateParams.processed_params.IMAGE) {
          delete templateParams.processed_params.IMAGE;
        }
        if (templateParams.processed_params.VIDEO) {
          delete templateParams.processed_params.VIDEO;
        }
        if (templateParams.processed_params.TEXT) {
          delete templateParams.processed_params.TEXT;
        }

        Object.keys(templateParams.processed_params).forEach(key => {
          if (key.startsWith('TEXT_')) {
            delete templateParams.processed_params[key];
          }
        });
      }
    }
    payload = {
      content_type: contentType,
      content: message,
      private: isPrivate,
      echo_id: echoId,
      content_attributes: contentAttributes,
      additional_attributes: additionalAttributes,
      cc_emails: ccEmails,
      bcc_emails: bccEmails,
      to_emails: toEmails,
      template_params: templateParams,
    };
  }
  return payload;
};

class MessageApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  create({
    conversationId,
    message,
    private: isPrivate,
    contentType,
    contentAttributes,
    additionalAttributes,
    echo_id: echoId,
    files,
    ccEmails = '',
    bccEmails = '',
    toEmails = '',
    templateParams,
  }) {
    return axios({
      method: 'post',
      url: `${this.url}/${conversationId}/messages`,
      data: buildCreatePayload({
        message,
        isPrivate,
        contentType,
        contentAttributes,
        additionalAttributes,
        echoId,
        files,
        ccEmails,
        bccEmails,
        toEmails,
        templateParams,
      }),
    });
  }

  delete(conversationID, messageId) {
    return axios.delete(`${this.url}/${conversationID}/messages/${messageId}`);
  }

  retry(conversationID, messageId) {
    return axios.post(
      `${this.url}/${conversationID}/messages/${messageId}/retry`
    );
  }

  getPreviousMessages({ conversationId, after, before }) {
    const params = { before };
    if (after && Number(after) !== Number(before)) {
      params.after = after;
    }
    return axios.get(`${this.url}/${conversationId}/messages`, { params });
  }

  getPreviousMessagesByContactId({ conversationId, after, before,contactId }) {
    const params = { before, contactId };
    if (after && Number(after) !== Number(before)) {
      params.after = after;
    }
    return axios.get(`${this.url}/${conversationId}/messages`, { params });
  }

  translateMessage(conversationId, messageId, targetLanguage) {
    return axios.post(
      `${this.url}/${conversationId}/messages/${messageId}/translate`,
      {
        target_language: targetLanguage,
      }
    );
  }
}

export default new MessageApi();
